import React from "react";
import PropTypes from "prop-types";
import { Empty, Spin, Icon, Input, Button } from "antd";
import SectionBlock from "./components/section-block";
import CardBlock from "./components/card-block";
import CamSummaryCards from "./components/cam-summary-cards";
import CamCategoryOverview from "./components/cam-category-overview";
import CamParameterTable from "./components/cam-parameter-table";
import { pendingActionRows } from "./cam-utils";
import { SECTION_IDS } from "./constants";

const FILTERS = [
    { key: "ALL", label: "All" },
    { key: "PASS", label: "Pass" },
    { key: "FAIL", label: "Fail" }
];

function sectionDomId(title) {
    return "cc-cam-sec-" + title.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
}

// Antd's icon set has no "leaf" glyph, so this is a small inline SVG —
// flags a case processed through the automation policy engine
// (caseDetail.cam_type === "EB Automation").
function EbAutomationBadge() {
    return (
        <span
            className={"cc-cam-eb-badge"}
            title={"Processed via EB Automation"}
        >
            <svg
                width={"14"}
                height={"14"}
                viewBox={"0 0 24 24"}
                fill={"currentColor"}
                aria-hidden={"true"}
            >
                <path d={"M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z"} />
            </svg>
            EB Automation
        </span>
    );
}

/**
 * Credit Assessment (CAM) section — a read-only port of the CAM View: headline
 * KPIs, a RAG category overview, and per-category parameter tables (policy /
 * value / pass-fail) with search + pass/fail filtering.
 */
class CamAssessmentSection extends React.Component {
    // Default the Quick filter to Failed — the failing checks are what a
    // reviewer needs to action first.
    state = { filter: "FAIL", query: "", open: {} };

    setFilter = filter => this.setState({ filter });

    onSearch = e => this.setState({ query: e.target.value });

    toggle = title =>
        this.setState(prev => ({
            open: { ...prev.open, [title]: !this.isOpen(title) }
        }));

    isOpen = (title, index) => {
        if (title in this.state.open) {
            return this.state.open[title];
        }
        return index === 0; // first category open by default
    };

    selectCategory = title => {
        this.setState(
            prev => ({ open: { ...prev.open, [title]: true } }),
            () => {
                const node = document.getElementById(sectionDomId(title));
                if (!node) {
                    return;
                }
                // Scroll within the Credit Checker's own scroll container and
                // offset by the sticky toolbar height, so the category header
                // lands just below it (not hidden underneath).
                const container = node.closest(".credit-checker__content");
                if (!container) {
                    if (node.scrollIntoView) {
                        node.scrollIntoView({
                            behavior: "smooth",
                            block: "start"
                        });
                    }
                    return;
                }
                const toolbar = container.querySelector(
                    ".credit-checker__toolbar"
                );
                const offset = (toolbar ? toolbar.offsetHeight : 0) + 12;
                const top =
                    node.getBoundingClientRect().top -
                    container.getBoundingClientRect().top +
                    container.scrollTop -
                    offset;
                container.scrollTo({
                    top: Math.max(top, 0),
                    behavior: "smooth"
                });
            }
        );
    };

    filterRows(rows) {
        const { filter, query } = this.state;
        const q = query.trim().toLowerCase();
        let result = rows;
        if (filter === "PASS") {
            result = result.filter(r => r.output === "Pass");
        } else if (filter === "FAIL") {
            result = result.filter(r => r.output === "Fail");
        }
        if (q) {
            result = result.filter(
                r =>
                    (r.factName || "").toLowerCase().indexOf(q) !== -1 ||
                    (r.policy || "").toLowerCase().indexOf(q) !== -1 ||
                    (r.value || "").toLowerCase().indexOf(q) !== -1
            );
        }
        return result;
    }

    render() {
        const {
            innerRef,
            cam,
            loading,
            expanded,
            onToggle,
            onRefresh,
            refreshing,
            stageId,
            onEditRemark,
            onUpdate,
            updating,
            camType
        } = this.props;
        // Same stage gating as the Credit Summary CAM (cam-view.js):
        // Refresh enabled for stages [4,5,18,20,7,8]; Update (and editing the
        // credit remarks it saves) enabled for stages [7,8] only.
        const refreshDisabled = ![4, 5, 18, 20, 7, 8].includes(stageId);
        const canEdit = [7, 8].includes(stageId);
        const hasData = cam && cam.hasData;
        const categories = (cam && cam.categories) || [];
        const filtered = categories
            .map(cat => ({ ...cat, rows: this.filterRows(cat.rows) }))
            .filter(cat => cat.rows.length);

        return (
            <SectionBlock
                id={SECTION_IDS.CAM_ASSESSMENT}
                innerRef={innerRef}
                title={"Credit Assessment"}
                subtitle={"Policy checks across categories — pass / fail verdicts"}
                icon={"safety-certificate"}
                expanded={expanded}
                onToggle={onToggle}
            >
                <Spin spinning={Boolean(loading)}>
                    {hasData ? (
                        <React.Fragment>
                            <CamSummaryCards summary={cam.summary} />

                            <div className={"cc-cam-bar"}>
                                <span className={"cc-cam-search"}>
                                    <Icon type={"search"} />
                                    <Input
                                        value={this.state.query}
                                        onChange={this.onSearch}
                                        placeholder={
                                            "Search parameters across categories…"
                                        }
                                    />
                                </span>
                                <span className={"cc-cam-filter-group"}>
                                    <span className={"cc-cam-filter-label"}>
                                        Quick filter:
                                    </span>
                                    <span className={"cc-cam-filter"}>
                                        {FILTERS.map(f => (
                                            <button
                                                type={"button"}
                                                key={f.key}
                                                className={
                                                    "cc-cam-filter__btn" +
                                                    (f.key === "FAIL"
                                                        ? " cc-cam-filter__btn--fail"
                                                        : "") +
                                                    (this.state.filter === f.key
                                                        ? " cc-cam-filter__btn--on"
                                                        : "")
                                                }
                                                onClick={() =>
                                                    this.setFilter(f.key)
                                                }
                                            >
                                                {f.key === "FAIL" ? (
                                                    <Icon
                                                        type={
                                                            "exclamation-circle"
                                                        }
                                                    />
                                                ) : null}
                                                {f.key === "FAIL"
                                                    ? `Failed (${cam.summary.fail})`
                                                    : f.label}
                                            </button>
                                        ))}
                                    </span>
                                </span>
                            </div>

                            <CardBlock
                                icon={"appstore"}
                                title={"Category Overview"}
                                subtitle={"RAG status per category"}
                                action={
                                    camType === "EB Automation" ? (
                                        <EbAutomationBadge />
                                    ) : null
                                }
                            >
                                <CamCategoryOverview
                                    categories={categories}
                                    onSelect={this.selectCategory}
                                />
                            </CardBlock>

                            {/* Update / Refresh — moved out of the section header
                                to sit just above the category cards (Applicant
                                Profile, …). */}
                            {onRefresh || (canEdit && onUpdate) ? (
                                <div className={"cc-cam-actions cc-cam-actions--bar"}>
                                    {canEdit && onUpdate ? (
                                        <Button
                                            size={"small"}
                                            type={"primary"}
                                            icon={"save"}
                                            loading={Boolean(updating)}
                                            onClick={onUpdate}
                                        >
                                            Update
                                        </Button>
                                    ) : null}
                                    {onRefresh ? (
                                        <Button
                                            size={"small"}
                                            icon={"reload"}
                                            loading={Boolean(refreshing)}
                                            disabled={refreshDisabled}
                                            onClick={onRefresh}
                                        >
                                            Refresh
                                        </Button>
                                    ) : null}
                                </div>
                            ) : null}

                            {filtered.length ? (
                                filtered.map((cat, index) => {
                                    const open = this.isOpen(cat.title, index);
                                    const tone = cat.statusKey.toLowerCase();
                                    const approved = cat.failed === 0;
                                    // Every failure here has already been
                                    // remarked on, so drop the action prompt.
                                    const showPill =
                                        approved ||
                                        pendingActionRows(cat.rows).length > 0;
                                    return (
                                        <div
                                            key={cat.title}
                                            id={sectionDomId(cat.title)}
                                            className={"cc-cam-cat"}
                                        >
                                            <button
                                                type={"button"}
                                                className={
                                                    "cc-cam-cat__head cc-cam-cat__head--" +
                                                    tone
                                                }
                                                onClick={() =>
                                                    this.toggle(cat.title)
                                                }
                                                aria-expanded={open}
                                            >
                                                <span
                                                    className={"cc-cam-cat__left"}
                                                >
                                                    <span
                                                        className={
                                                            "cc-cam-cat__icon"
                                                        }
                                                    >
                                                        <Icon type={cat.icon} />
                                                    </span>
                                                    <span
                                                        className={
                                                            "cc-cam-cat__title"
                                                        }
                                                    >
                                                        {cat.title}
                                                    </span>
                                                    {showPill ? (
                                                        <span
                                                            className={
                                                                "cc-cam-cat__pill cc-cam-cat__pill--" +
                                                                (approved
                                                                    ? "ok"
                                                                    : "act")
                                                            }
                                                        >
                                                            <Icon
                                                                type={
                                                                    approved
                                                                        ? "check-circle"
                                                                        : "exclamation-circle"
                                                                }
                                                            />
                                                            {approved
                                                                ? "Approved"
                                                                : "Requires Action"}
                                                        </span>
                                                    ) : null}
                                                </span>
                                                <span
                                                    className={
                                                        "cc-cam-cat__right"
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            "cc-cam-cat__count cc-cam-cat__count--pass"
                                                        }
                                                    >
                                                        <Icon
                                                            type={"check-circle"}
                                                        />
                                                        {cat.passed} Pass
                                                    </span>
                                                    <span
                                                        className={
                                                            "cc-cam-cat__count cc-cam-cat__count--fail"
                                                        }
                                                    >
                                                        <Icon
                                                            type={"close-circle"}
                                                        />
                                                        {cat.failed} Fail
                                                    </span>
                                                    <Icon
                                                        type={"down"}
                                                        className={
                                                            "cc-cam-cat__chev" +
                                                            (open
                                                                ? " cc-cam-cat__chev--open"
                                                                : "")
                                                        }
                                                    />
                                                </span>
                                            </button>
                                            {open ? (
                                                <div
                                                    className={"cc-cam-cat__body"}
                                                >
                                                    <CamParameterTable
                                                        rows={cat.rows}
                                                        editable={canEdit}
                                                        onEditRemark={
                                                            onEditRemark
                                                        }
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })
                            ) : (
                                <Empty
                                    description={
                                        "No parameters match the search / filter"
                                    }
                                />
                            )}
                        </React.Fragment>
                    ) : (
                        !loading && (
                            <Empty
                                description={"Credit assessment not available"}
                            />
                        )
                    )}
                </Spin>
            </SectionBlock>
        );
    }
}

CamAssessmentSection.propTypes = {
    innerRef: PropTypes.object,
    cam: PropTypes.object,
    loading: PropTypes.bool,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    onRefresh: PropTypes.func,
    refreshing: PropTypes.bool,
    stageId: PropTypes.number,
    onEditRemark: PropTypes.func,
    onUpdate: PropTypes.func,
    updating: PropTypes.bool,
    // caseDetail.cam_type (records/case-record.js) — "EB Automation" shows
    // the leaf badge on the Category Overview card.
    camType: PropTypes.string
};

export default CamAssessmentSection;
